import React, { FC, useEffect } from 'react'
import * as StyledList from './filteredProductsList.styles'
import StandardHero from 'components/hero/standardHero'
import ProductList from 'components/products/productList'
import ErrorMessage from 'components/errorMessage'
import useProductsFiltered from 'hooks/useProductsFiltered'
import { useDispatch } from 'react-redux'
import { toggleMobileFiltersMenu } from 'reducers/catNavReducer'
import useFilters from 'hooks/useFilters'
import useFiltersUrlQueryParams from 'hooks/useFiltersUrlQueryParams'
import { AllergenFilter } from './allergenFilterList'
import { useScrollToTopOnFirstPage, getActiveDisplayTreeFilter, getActiveMainTreeFilter } from './helpers'
import { StorefrontCategoryTreeItem } from '@local-types/categoryTrees/storefrontCategoryTree'
import ProductFilters from './productFilters'
import { Flex } from 'rebass'
import { H2 } from 'components/base/typography/headings'
import FilteredProductList from 'interfaces/prismic/filteredProductList'
// @ts-ignore
import { RichText } from 'prismic-reactjs'
import TogglableMenu from 'components/togglableMenu'
import useTypedSelector from 'interfaces/useTypedSelector'
import IconFilters from 'icons/filters.svg'
import Basket from 'components/basket'
import { setBasketFixedState, setBasketVisibleState } from 'reducers/basketReducer'
import ProductFilterChips from './productFilters/productFilterChips'
import BreadCrumbs from 'components/breadCrumbs'
import { getBreadCrumbsByCategoryId } from 'helpers/breadCrumbsHelper/breadCrumbsHelper'
import LoadNext from './loadMore/loadNext'
import LoadPrevious from './loadMore/loadPrevious'
import { IconButton } from 'components/base/buttons/buttons'

interface Props {
	page: FilteredProductList | null
}

const FilteredProductsList: FC<Props> = ({ page: contentPage }) => {
	const dispatch = useDispatch()
	const { mobileFiltersOpen } = useTypedSelector(({ catNav }) => catNav)
	const {
		filters,
		aggregatedFilters,
		activeFilters,
		setMainCategoryTreePath,
		setFilterTreeIds,
		setAllergenFilterIds,
	} = useFilters()
	const {
		products,
		loading,
		lazyLoading,
		error,
		totalRecords,
		status,
		page,
		pageSize,
		fetchNextPage,
		fetchPreviousPage,
	} = useProductsFiltered()
	const mainTreeFilter = getActiveDisplayTreeFilter(activeFilters?.catTreePath, filters?.mainCategoryTree)
	const breadCrumbsTreeFilter = getActiveMainTreeFilter(activeFilters?.catTreePath, filters?.mainCategoryTree)
	const { nextPagePath, previousPagePath } = useFiltersUrlQueryParams()
	useScrollToTopOnFirstPage(page)

	useEffect(() => {
		dispatch(setBasketVisibleState(false))
		dispatch(setBasketFixedState(true))
		return () => {
			dispatch(setBasketFixedState(false))
		}
	}, [])

	const handleMainTreePrevClick = () => {
		const updatedPath = [...activeFilters.catTreePath].slice(0, activeFilters.catTreePath.length - 1)
		setMainCategoryTreePath(updatedPath)
	}

	const handleMainTreeItemClick = (
		treeItem: StorefrontCategoryTreeItem,
		activeSiblings?: StorefrontCategoryTreeItem[],
		activeParents?: StorefrontCategoryTreeItem[]
	) => {
		let ids = [...activeFilters.catTreePath]
		if (activeSiblings) {
			const activeSiblingIds = activeSiblings.map(c => c.id)
			ids = ids.filter(id => !activeSiblingIds.includes(id))
			if (activeSiblingIds.includes(treeItem.id)) {
				ids = ids.filter(x => x !== treeItem.id)
				setMainCategoryTreePath(ids)
				return
			}
		}

		if (activeParents) {
			const activeParentIds = activeParents.map(c => c.id)
			ids = ids.filter(id => !activeParentIds.includes(id))
		}

		if (ids.includes(treeItem.categoryId)) {
			ids = ids.filter(x => x !== treeItem.categoryId)
		} else if (filters?.mainCategoryTree?.children.length === 0) {
			ids = ids.slice(0, ids.length - 1).concat([treeItem.categoryId])
		} else {
			ids = ids.concat([treeItem.categoryId])
		}

		setMainCategoryTreePath(ids)
	}

	const clearActiveSubfilters = (activeSiblings?: StorefrontCategoryTreeItem[]) => {
		let ids = [...activeFilters.catTreePath]
		if (activeSiblings) {
			const activeSiblingIds = activeSiblings.map(c => c.id)
			ids = ids.filter(id => !activeSiblingIds.includes(id))
		}

		setMainCategoryTreePath(ids)
	}

	const handleFilterTreeItemClick = (treeItem: StorefrontCategoryTreeItem) => {
		const ids = [...activeFilters.catTreeFilterIds]
		if (ids.includes(treeItem.categoryId)) setFilterTreeIds(ids.filter(x => x !== treeItem.categoryId))
		else setFilterTreeIds(ids.concat([treeItem.categoryId]))
	}

	const handleAllergenFilterClick = (allergenFilter: AllergenFilter) => {
		const ids = [...activeFilters.allergenFilterIds]
		if (ids.includes(allergenFilter.id)) setAllergenFilterIds(ids.filter(x => x !== allergenFilter.id))
		else setAllergenFilterIds(ids.concat([allergenFilter.id]))
	}

	const clearAllergenFilters = () => {
		setAllergenFilterIds([])
	}

	const handleMobileFiltersButton = () => dispatch(toggleMobileFiltersMenu(!mobileFiltersOpen))

	if (error)
		return (
			<ErrorMessage
				title="Tókst ekki að sækja vöruflokk"
				text="Vinsamlegast reynið aftur síðar eða hafið samband við þjónustu"
			/>
		)

	return (
		<Flex>
			<StyledList.ListWrapper>
				<StandardHero
					title={contentPage?.data?.title ? RichText.asText(contentPage.data.title) : 'Vörulisti'}
					image={contentPage?.data?.hero_image ? contentPage.data.hero_image : '/images/veggie-bbq.webp'}
					desktopOnly
					short
				/>
				<BreadCrumbs
					onFilteredProductListPage
					items={[
						...getBreadCrumbsByCategoryId(
							breadCrumbsTreeFilter?.item?.id || '',
							filters?.mainCategoryTree || undefined
						),
					]}
				/>
				<StyledList.MobileFiltersWrapper>
					<ProductFilterChips
						mainTreeFilter={mainTreeFilter}
						activeFilters={activeFilters}
						filters={filters}
						handleMainTreeItemClick={handleMainTreeItemClick}
						handleFilterTreeItemClick={handleFilterTreeItemClick}
					/>
				</StyledList.MobileFiltersWrapper>
				<StyledList.Wrapper>
					<StyledList.InnerWrapper>
						<Flex justifyContent="space-between">
							<StyledList.TitleTreeWrapper>
								<H2 mb="5.5rem">{mainTreeFilter.isRoot ? 'Allir flokkar' : mainTreeFilter.item?.name}</H2>
							</StyledList.TitleTreeWrapper>
							<StyledList.MobileFilterButtonWrapper>
								<IconButton primary onClick={handleMobileFiltersButton}>
									<IconFilters />
								</IconButton>
							</StyledList.MobileFilterButtonWrapper>
						</Flex>
						<Flex>
							<TogglableMenu active={mobileFiltersOpen}>
								<StyledList.FilterColumn>
									<ProductFilters
										mainTreeFilter={mainTreeFilter}
										activeFilters={activeFilters}
										filters={aggregatedFilters}
										handleMainTreePrevClick={handleMainTreePrevClick}
										handleMainTreeItemClick={handleMainTreeItemClick}
										handleFilterTreeItemClick={handleFilterTreeItemClick}
										handleAllergenFilterClick={handleAllergenFilterClick}
										clearAllergenFilters={clearAllergenFilters}
									/>
								</StyledList.FilterColumn>
							</TogglableMenu>
							<StyledList.ListColumn>
								<LoadPrevious
									products={products.length}
									totalProducts={totalRecords}
									page={page}
									pageSize={pageSize}
									loading={lazyLoading}
									previousPagePath={previousPagePath}
									handleLoadMore={fetchPreviousPage}
								/>
								<ProductList
									products={products}
									loading={loading}
									error={error}
									empty={products.length === 0 && status === 'fulfilled'}
								/>
								<LoadNext
									products={products.length}
									totalProducts={totalRecords}
									page={page}
									pageSize={pageSize}
									loading={lazyLoading}
									handleLoadMore={fetchNextPage}
									nextPagePath={nextPagePath}
								/>
							</StyledList.ListColumn>
						</Flex>
					</StyledList.InnerWrapper>
				</StyledList.Wrapper>
			</StyledList.ListWrapper>
			<StyledList.BasketContainer>
				<StyledList.BasketFixed>
					<Basket disableHideBasket />
				</StyledList.BasketFixed>
			</StyledList.BasketContainer>
		</Flex>
	)
}

export default FilteredProductsList
